import axios, { AxiosHeaders } from "axios";

export const PR_ROUTING_PARAM = "prRouting";
export const PR_ROUTING_CONFIG_REF_PARAM = "prRoutingConfigRef";
export const ROUTING_ID_HEADER = "x-noom-routing-id";
export const CONFIG_REF_HEADER = "x-noom-config-ref";

const SESSION_STORAGE_KEY = "prDeployRouting";
const CONFIG_REF_PREFIX = "routing-config-for-";
const OFF_VALUES = new Set(["", "0", "false", "none", "off"]);
const ROUTING_ID_PATTERN = /^[a-z0-9][a-z0-9._-]*$/i;
const CONFIG_REF_PATTERN = /^[a-z0-9][a-z0-9._/-]*$/i;
const NOOM_HOST_SUFFIXES = ["noom.com", "noomtest.com", "wsli.dev"];

export type PrDeployRouting = {
  routingId: string;
  configRef: string;
};

function isValidRouting(routing: PrDeployRouting): boolean {
  return (
    ROUTING_ID_PATTERN.test(routing.routingId) &&
    CONFIG_REF_PATTERN.test(routing.configRef)
  );
}

function readStoredRouting(): PrDeployRouting | null {
  try {
    const value = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!value) {
      return null;
    }

    const routing = JSON.parse(value) as PrDeployRouting;
    return isValidRouting(routing) ? routing : null;
  } catch {
    return null;
  }
}

function storeRouting(routing: PrDeployRouting | null): void {
  try {
    if (routing) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(routing));
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // Routing still works for the current page when storage is unavailable.
  }
}

function resolveRouting(): PrDeployRouting | null {
  const params = new URLSearchParams(window.location.search);
  const rawRoutingId = params.get(PR_ROUTING_PARAM)?.trim();

  if (
    rawRoutingId !== undefined &&
    rawRoutingId !== null &&
    OFF_VALUES.has(rawRoutingId.toLowerCase())
  ) {
    storeRouting(null);
    return null;
  }

  if (process.env.HIVE_NOOM_ENVIRONMENT === "production") {
    return null;
  }

  if (!rawRoutingId) {
    return readStoredRouting();
  }

  const configRef =
    params.get(PR_ROUTING_CONFIG_REF_PARAM)?.trim() ||
    `${CONFIG_REF_PREFIX}${rawRoutingId}`;
  const routing = { routingId: rawRoutingId, configRef };

  if (!isValidRouting(routing)) {
    storeRouting(null);
    return null;
  }

  storeRouting(routing);
  return routing;
}

let resolvedRouting: PrDeployRouting | null | undefined;

export function getPrDeployRouting(): PrDeployRouting | null {
  if (resolvedRouting === undefined) {
    resolvedRouting = resolveRouting();
  }
  return resolvedRouting;
}

export function getPrDeployRoutingHeaders(): Record<string, string> {
  const routing = getPrDeployRouting();
  if (!routing) {
    return {};
  }

  return {
    [ROUTING_ID_HEADER]: routing.routingId,
    [CONFIG_REF_HEADER]: routing.configRef,
  };
}

function isNoomUrl(url: string, baseUrl?: string): boolean {
  let hostname: string;
  try {
    const base = baseUrl
      ? new URL(baseUrl, window.location.href).toString()
      : window.location.href;
    hostname = new URL(url, base).hostname;
  } catch {
    return false;
  }

  return (
    hostname === window.location.hostname ||
    NOOM_HOST_SUFFIXES.some(
      (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`)
    )
  );
}

let axiosInterceptorId: number | undefined;

export function installPrDeployRoutingAxiosInterceptor(): void {
  if (axiosInterceptorId !== undefined) {
    return;
  }

  axiosInterceptorId = axios.interceptors.request.use((config) => {
    const routingHeaders = getPrDeployRoutingHeaders();
    if (
      Object.keys(routingHeaders).length === 0 ||
      !config.url ||
      !isNoomUrl(config.url, config.baseURL)
    ) {
      return config;
    }

    const headers = AxiosHeaders.from(config.headers);
    Object.entries(routingHeaders).forEach(([name, value]) => {
      if (!headers.has(name)) {
        headers.set(name, value);
      }
    });

    return { ...config, headers };
  });
}

export function resetPrDeployRoutingForTests(): void {
  resolvedRouting = undefined;
  if (axiosInterceptorId !== undefined) {
    axios.interceptors.request.eject(axiosInterceptorId);
    axiosInterceptorId = undefined;
  }
}
